import { Grid, makeStyles, Typography } from "@material-ui/core";
import React from "react";
import { useSelector } from "react-redux";
import { ResponseAttribute, State } from "../../../type";
import { AddNewButton } from "../../user/share_feedback/addNewButton";
import { FeedbackHeaderText } from "./FeedbackHeader";
import { CustomDivider } from "./LocalDivider";
import { MemberChip } from "./MemberChip";
import { FeedbackSectionProps } from "./type";
import { Editor } from "react-draft-wysiwyg";
import { EditorState } from "draft-js";
import { handleDraftEditorPastedText, onDraftEditorCopy, onDraftEditorCut } from "draftjs-conductor";
import { RemainingCharWarnText } from "../../user/share_feedback/addNewButtonElements";
import { MAX_FIELD_CHAR_LIMIT } from "../../../util/feedbackValidation";
import { useNewFlowEditors, EditorField } from "./useNewFlowEditors";

const TOOLBAR_CONFIG = {
  options: ["inline", "link", "emoji", "list"],
  inline: { options: ["bold", "italic", "underline"] },
  link: { inDropdown: false, options: ["link", "unlink"] },
  list: { options: ["unordered"] },
};

const useStyles = makeStyles((theme) => ({
  feedbackArea: {
    border: "1px solid #DCDCDC",
    borderRadius: theme.spacing(0.5),
    backgroundColor: "#FFFFFF",
    boxShadow: "0 2px 4px 4px rgba(191,191,191,0.3)",
    marginTop: theme.spacing(7.5),
  },
  memberHeading: {
    fontSize: theme.spacing(3),
    marginTop: theme.spacing(5),
  },
  commonTextStyle: {
    textAlign: "justify",
    fontFamily: "Montserrat",
    fontWeight: 600,
    color: "#272727",
    fontSize: theme.spacing(3),
    padding: theme.spacing(0, 3, 0, 3),
  },
  contextStyle: {
    fontSize: theme.spacing(1.75),
    fontWeight: 500,
    margin: theme.spacing(3.75, 0, 5, 0),
  },
  attributeHintTextStyle: {
    textAlign: "justify",
    fontFamily: "Montserrat",
    fontWeight: 500,
    color: "#868686",
    fontSize: theme.spacing(2),
    padding: theme.spacing(0, 3, 0, 3),
    marginTop: theme.spacing(1.75),
  },
  editorLabel: {
    fontSize: "14px",
    fontWeight: 600,
    marginTop: "16px",
  },
  editorContainer: {
    border: "1px solid #dfe2e5",
    borderRadius: "8px",
    padding: "8px",
    marginTop: "8px",
  },
}));

export const FeedbackSection: React.FC<FeedbackSectionProps> = ({
  attributes,
  setAttributes,
  shareFeedbackData,
  setShareFeedbackData,
  emojiSelected,
  setEmojiSelected,
  setValue,
  context,
  user,
  isAnyAttribute,
  onAnyChange,
  isMissing,
  isNewFlow,
  whatWentWell,
  whatCouldBeDifferent,
  overallFeedback,
  newFlowWhatWentWellHtml,
  setNewFlowWhatWentWellHtml,
  newFlowWhatCouldBeDifferentHtml,
  setNewFlowWhatCouldBeDifferentHtml,
  newFlowOverallFeedbackHtml,
  setNewFlowOverallFeedbackHtml,
}) => {
  const classes = useStyles();
  const attr: Array<ResponseAttribute> = [];
  const attLabels =
    useSelector(
      (state: State) => state.attributesDataForClient?.data?.attributes
    ) ?? attr;

  const editors = useNewFlowEditors(whatWentWell, whatCouldBeDifferent, overallFeedback);

  const handleEditorChange = (field: EditorField, setParentHtml?: (h: string) => void) => (state: EditorState) => {
    field.setEditorState(state);
    const html = editors.toHtml(state);
    field.setHtml(html);
    if (setParentHtml) setParentHtml(html);
    onAnyChange(true);
  };

  const handlePaste = (field: EditorField, setParentHtml?: (h: string) => void) => (text: string, html2: string | undefined, es: EditorState) => {
    const newState = handleDraftEditorPastedText(html2, es);
    if (newState) {
      field.setEditorState(newState);
      const html = editors.toHtml(newState);
      field.setHtml(html);
      if (setParentHtml) setParentHtml(html);
      onAnyChange(true);
      return true;
    }
    return false;
  };

  const renderNewFlowEditor = (
    label: string,
    field: EditorField,
    setParentHtml?: (h: string) => void
  ) => (
    <Grid xs={12} item style={{ padding: "0 24px" }}>
      <Typography className={classes.commonTextStyle} classes={{ root: classes.editorLabel }}>
        {label}<span style={{ color: "#DC0101", fontSize: "14px" }}> (required)</span>{" "}
      </Typography>
      <div className={classes.editorContainer}>
        <Editor
          editorState={field.editorState}
          placeholder="Type here..."
          toolbar={TOOLBAR_CONFIG}
          onEditorStateChange={handleEditorChange(field, setParentHtml)}
          onCopy={onDraftEditorCopy}
          onCut={onDraftEditorCut}
          handlePastedText={handlePaste(field, setParentHtml)}
        />
      </div>
      <RemainingCharWarnText text={field.html || ""} limit={MAX_FIELD_CHAR_LIMIT} />
    </Grid>
  );

  return (
    <Grid
      container
      justify="flex-start"
      alignItems="center"
      className={classes.feedbackArea}
    >
      <Grid xs={12} item>
        <FeedbackHeaderText />
      </Grid>
      <Grid xs={12} item>
        <Typography
          className={`${classes.commonTextStyle} ${classes.memberHeading}`}
        >
          Member
        </Typography>
      </Grid>
      <Grid xs={12} item>
        <MemberChip user={user} />
      </Grid>
      <Grid xs={12} item>
        <CustomDivider />
      </Grid>
      <Grid xs={12} item>
        <Typography
          className={`${classes.commonTextStyle} ${classes.memberHeading}`}
        >
          Context
        </Typography>
      </Grid>
      <Grid xs={12} item>
        <Typography
          className={`${classes.commonTextStyle} ${classes.contextStyle}`}
          dangerouslySetInnerHTML={{
            __html: context ? context : "",
          }}
        />
      </Grid>
      <Grid xs={12} item>
        <CustomDivider />
      </Grid>
      {isNewFlow ? (
        <>
          {renderNewFlowEditor("What went well?", editors.whatWentWell, setNewFlowWhatWentWellHtml)}
          {renderNewFlowEditor("What could have been done differently?", editors.whatCouldBeDifferent, setNewFlowWhatCouldBeDifferentHtml)}
          {renderNewFlowEditor("Overall Feedback, if any.", editors.overallFeedback, setNewFlowOverallFeedbackHtml)}
        </>
      ) : (
        <>
          <Grid xs={12} item>
            <Typography
              className={`${classes.commonTextStyle} ${classes.memberHeading}`}
            >
              Attributes
            </Typography>
          </Grid>
          <Grid xs={12} item>
            <Typography className={`${classes.attributeHintTextStyle}`}>
              <span style={isAnyAttribute ? { color: "#DC0101" } : {}}>
                Attributes to share feedback on
              </span>
            </Typography>
          </Grid>
          <Grid xs={12} item>
            <Grid container justify="flex-start">
              <AddNewButton
                attributes={attributes}
                setAttributes={setAttributes}
                shareFeedbackData={shareFeedbackData}
                setShareFeedbackData={setShareFeedbackData}
                emojiSelected={emojiSelected}
                setEmojiSelected={setEmojiSelected}
                setValue={setValue}
                onAnyChange={onAnyChange}
                isMissing={isMissing}
                attLabels={attLabels}
                from={"CLIENT"}
              />
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  );
};
